import {useAuth} from '@apps-common/auth-react';
import {Box, useColorScheme} from '@mui/material';
import * as Sentry from '@sentry/react';
import {useGeographic} from 'ol/proj';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate, Route, Routes} from 'react-router';
import {setOverlayOn, setUserOverlayUrl} from '../actions/map.js';
import {hideModal, resize, showModal} from '../actions/ui.js';
import {MODALS} from '../constants/index.js';
import AsyncUpload from './AsyncUpload.jsx';
import Details from './DetailsPanel/Details.jsx';
import WelcomeDialog from './Dialogs/WelcomeDialog.jsx';
import ModalsManager from './Dialogs/index.jsx';
import FileDrop from './FileDrop.jsx';
import LeftNav from './LeftNavBar.jsx';
import Notifier from './Notifier/index.jsx';
import SearchInput from './SearchInput.jsx';
import Sidebars from './Sidebars.jsx';
import Tour from './Tour/index.jsx';
import Viewer from './Viewer/index.jsx';
import ShiftContainer from './common/ShiftContainer.jsx';
import useOrgParams from '@app/hooks/useOrgParams';
import {THEME_MODE} from '@app/styles/index.jsx';
import {isUsersOrg} from '@app/util/user';

// eslint-disable-next-line react-hooks/rules-of-hooks
useGeographic();

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => {
  const dispatch = useDispatch();
  const {setMode} = useColorScheme();

  const themeModeLabOn = useSelector(state => state.labs.themeMode);

  const leftDrawer = useSelector(state => state.ui.drawers.left);
  const rightDrawer = useSelector(state => state.ui.drawers.right);
  const activeModal = useSelector(state => state.ui.modal);

  const [isDragging, setIsDragging] = useState(false);
  const [inFiles, setInFiles] = useState({});

  const containerRef = useRef();

  const {user} = useAuth();
  const {orgId} = useOrgParams();

  const isOwnOrg = isUsersOrg(user.claims, orgId);

  const onResize = useCallback(() => {
    const node = containerRef.current;
    if (node) {
      dispatch(
        resize({
          width: node.offsetWidth,
          height: node.offsetHeight,
        })
      );
    }
  }, [dispatch]);

  const onDragEnter = event => {
    if (!isOwnOrg) {
      return;
    }

    const types = event.dataTransfer.types;
    for (let i = 0; i < types.length; i++) {
      if (types[i] === 'Files') {
        setIsDragging(true);
        break;
      }
    }
  };

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      setIsDragging(false);
      setInFiles({
        acceptedFiles,
        rejectedFiles,
      });

      dispatch(showModal(MODALS.UPLOAD));
    },
    [dispatch, setIsDragging, setInFiles]
  );

  useEffect(() => {
    window.addEventListener('resize', onResize);

    window.addEventListener(
      'dragover',
      function (e) {
        e.preventDefault();
      },
      false
    );
    window.addEventListener(
      'drop',
      e => {
        e.preventDefault();
        const items = e.dataTransfer.items;
        if (items.length > 0) {
          if (items[0].kind === 'string') {
            // probability that this is a URL.
            items[0].getAsString(itemString => {
              // oh, yeah, definitely a URL
              if (itemString.indexOf('http') === 0) {
                dispatch(setUserOverlayUrl(itemString));
                dispatch(setOverlayOn({name: 'user', on: true}));
              }
            });
          }
        }
      },
      false
    );

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [dispatch, onResize]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onResize, []);

  useEffect(() => {
    if (!themeModeLabOn) {
      setMode(THEME_MODE.DARK); // default to dark for pipped-fm
    }
  }, [setMode, themeModeLabOn]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
        }}
      >
        <LeftNav />
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
            }}
          >
            <Sidebars />
            <SentryRoutes>
              <Route
                path="/"
                element={
                  <Box
                    ref={containerRef}
                    onDragEnter={onDragEnter}
                    sx={{
                      bgcolor: '#2C2C2B',
                    }}
                  >
                    <DndProvider backend={HTML5Backend}>
                      <Viewer
                        leftDrawer={leftDrawer}
                        rightDrawer={rightDrawer}
                      />
                      <ShiftContainer
                        left={8}
                        top={8}
                        position="absolute"
                        zIndex={1}
                      >
                        <SearchInput />
                      </ShiftContainer>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '1em',
                          top: 8,
                        }}
                      >
                        <Details />
                      </Box>
                    </DndProvider>
                  </Box>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </SentryRoutes>
          </Box>
        </Box>
      </Box>
      <Notifier />
      <ModalsManager />
      <AsyncUpload
        open={activeModal === MODALS.UPLOAD}
        onClose={() => {
          dispatch(hideModal());
        }}
        inFiles={inFiles}
      />
      <WelcomeDialog inFiles={inFiles} setInFiles={setInFiles} />
      {isOwnOrg && (
        <FileDrop
          isDragging={isDragging}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        />
      )}
      <Tour />
    </>
  );
};

export default App;
